import { React, useEffect, useState, useMemo } from 'react';
import {
  FiSettings,
  FiCheckSquare,
  FiTrash2,
  FiPlus,
  FiX,
  FiSave,
  FiEdit3,
  FiAlertTriangle,
  FiArchive,
  FiInbox,
  FiPlusCircle,
} from 'react-icons/fi';
import toast from 'react-hot-toast';

import { useEmails } from '../../context/EmailsProvider';
import { DataState } from '../../context/DataProvider';
import { API_URL } from '../../services/api';

const EmailLabels = ({
  emailStats = {},
  emailfilter = {},
  setEmailFilter,
  handleFilterChange,
}) => {
  const { isOutlook, isGmailAuthenticated, isOutlookAuthenticated } =
    useEmails();

  const { userGoogleId } = DataState();

  const configuredObjectivesFromServer = useMemo(
    () => emailStats.objective_classes || [],
    [emailStats],
  );
  const objectiveClassCountsFromStats = useMemo(
    () => emailStats.objective_class_counts || {},
    [emailStats],
  );
  const garbageObjectivesFromServer = useMemo(
    () => emailStats.garbage_objective_classes || [],
    [emailStats],
  );
  const garbageCountsFromServer = useMemo(
    () => emailStats.garbage_objective_class_counts || {},
    [emailStats],
  );
  const senderClassCounts = emailStats.sender_class_counts || {};

  const [objectiveClassItems, setObjectiveClassItems] = useState([]);
  const [garbageCategoriesList, setGarbageCategoriesList] = useState([]); // For temporarily holding processed garbage items

  const [isManagingCategories, setIsManagingCategories] = useState(false);
  const [showAddCategoryForm, setShowAddCategoryForm] = useState(false);
  const [showGarbageUI, setShowGarbageUI] = useState(false); // Toggle for garbage UI

  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [
    newCategoryDescriptionManuallyEdited,
    setNewCategoryDescriptionManuallyEdited,
  ] = useState(false);

  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
  const [modalWarningMessage, setModalWarningMessage] = useState('');

  // Effect to initialize/sync all objective class states from props
  useEffect(() => {
    // Process active configured objectives
    const activeItemsFromProps = (configuredObjectivesFromServer || []).map(
      (cfg, index) => ({
        id: `active-${cfg.name}-${index}`, // Base ID on name for potential matching
        name: cfg.name,
        description: cfg.description,
        value: cfg.name,
        count: objectiveClassCountsFromStats[cfg.name] || 0,
        filterKey: 'objectiveClass',
        originalName: cfg.name,
        isEditing: false,
        tempName: cfg.name,
        tempDescription: cfg.description,
      }),
    );

    // Process garbage/deleted objectives
    const rawGarbageItems = (garbageObjectivesFromServer || [])
      .map((gbg, index) => ({
        id: `garbage-${gbg.name}-${index}`,
        name: gbg.name,
        description: gbg.description,
        count: garbageCountsFromServer?.[gbg.name] || 0,
      }))
      .sort((a, b) => b.count - a.count); // Sort by count descending

    // Smart merging for active items: update from props, preserve local additions/edits
    setObjectiveClassItems((prevLocalActiveItems) => {
      const updatedActiveItems = activeItemsFromProps.map((serverItem) => {
        const localMatch = prevLocalActiveItems.find(
          (li) =>
            li.originalName === serverItem.originalName &&
            li.originalName !== null,
        );
        if (localMatch) {
          return {
            ...serverItem, // Base with server data
            id: localMatch.id, // Keep stable local ID
            name: localMatch.isEditing ? localMatch.tempName : localMatch.name, // Keep local edit if active
            description: localMatch.isEditing
              ? localMatch.tempDescription
              : localMatch.description,
            value: localMatch.isEditing ? localMatch.tempName : localMatch.name,
            isEditing: localMatch.isEditing, // Preserve edit state
            tempName: localMatch.tempName,
            tempDescription: localMatch.tempDescription,
          };
        }
        return serverItem; // New item from server config
      });

      // Keep purely user-added items (originalName is null)
      const purelyUserAdded = prevLocalActiveItems.filter(
        (local) =>
          local.filterKey === 'objectiveClass' && local.originalName === null,
      );
      return [...updatedActiveItems, ...purelyUserAdded];
    });

    setGarbageCategoriesList(rawGarbageItems);
  }, [
    configuredObjectivesFromServer,
    objectiveClassCountsFromStats,
    garbageObjectivesFromServer,
    garbageCountsFromServer,
  ]);

  useEffect(() => {
    /* ... default description logic ... */
    if (!newCategoryDescriptionManuallyEdited && newCategoryName) {
      setNewCategoryDescription(`The email is related to '${newCategoryName}'`);
    } else if (!newCategoryName && !newCategoryDescriptionManuallyEdited) {
      setNewCategoryDescription('');
    }
  }, [newCategoryName, newCategoryDescriptionManuallyEdited]);

  const staticItems = [
    /* ... same ... */
    {
      name: 'Urgent',
      filterKey: 'urgency',
      value: true,
      count: emailStats.urgent_count || 0,
    },
    {
      name: 'Stakeholder',
      filterKey: 'senderClass',
      value: 'stakeholder',
      count: senderClassCounts['stakeholder'] || 0,
    },
    {
      name: 'Vendor',
      filterKey: 'senderClass',
      value: 'vendor',
      count: senderClassCounts['vendor'] || 0,
    },
    {
      name: 'Client',
      filterKey: 'senderClass',
      value: 'client',
      count: senderClassCounts['client'] || 0,
    },
    {
      name: 'Team',
      filterKey: 'senderClass',
      value: 'team',
      count: senderClassCounts['team'] || 0,
    },
    {
      name: 'Other Sender',
      filterKey: 'senderClass',
      value: 'other',
      count: senderClassCounts['other'] || 0,
    },
    {
      name: 'Unread',
      filterKey: 'isRead',
      value: false,
      count: emailStats.unread_count || 0,
    },
    {
      name: 'Not Replied',
      filterKey: 'threads',
      value: 'not-replied',
      count: emailStats.non_replied_count || 0,
    },
  ];

  const allDisplayItems = [...staticItems, ...objectiveClassItems];

  const handleToggleManagingCategories = () => {
    /* ... (same, consider unsaved changes warning) ... */
    if (isManagingCategories) {
      const hasUnsavedChanges =
        objectiveClassItems.some(
          (item) =>
            item.name !== item.originalName ||
            (item.originalName &&
              item.description !==
                configuredObjectivesFromServer.find(
                  (co) => co.name === item.originalName,
                )?.description) ||
            item.originalName === null, // Purely new item
        ) ||
        objectiveClassItems.filter((item) => item.originalName !== null)
          .length !== configuredObjectivesFromServer.length;

      if (hasUnsavedChanges) {
        if (
          !window.confirm(
            'You have unsaved changes. Are you sure you want to exit management mode? Your changes will be lost.',
          )
        ) {
          return;
        }
      }
      // Simplified reset: re-trigger effect by clearing and then relying on prop-based re-initialization.
      // Or, more carefully revert items to their 'originalName' and server description.
      // For now, exiting just closes UI, Save All Changes is the explicit save.
      // Let's revert editing states
      setObjectiveClassItems((items) =>
        items.map((item) => ({
          ...item,
          isEditing: false,
          name: item.originalName || item.name,
          description:
            configuredObjectivesFromServer.find(
              (co) => co.name === (item.originalName || item.name),
            )?.description || item.description,
          tempName: item.originalName || item.name,
          tempDescription:
            configuredObjectivesFromServer.find(
              (co) => co.name === (item.originalName || item.name),
            )?.description || item.description,
          value: item.originalName || item.name,
        })),
      );
    }
    setIsManagingCategories(!isManagingCategories);
    setShowAddCategoryForm(false);
    setShowGarbageUI(false); // Close garbage UI when exiting main manage mode
  };

  const handleShowAddCategoryForm = () => {
    /* ... same ... */
    setShowAddCategoryForm(true);
    setNewCategoryName('');
    setNewCategoryDescription('');
    setNewCategoryDescriptionManuallyEdited(false);
  };
  const handleAddNewCategory = () => {
    /* ... same ... */
    const trimmedName = newCategoryName.trim();
    if (!trimmedName) {
      alert('Category name cannot be empty.');
      return;
    }
    if (
      objectiveClassItems.some(
        (item) => item.name.toLowerCase() === trimmedName.toLowerCase(),
      )
    ) {
      alert(`A category named "${trimmedName}" already exists.`);
      return;
    }
    const newCategory = {
      id: `${Date.now()}-new`,
      name: trimmedName,
      description:
        newCategoryDescription.trim() ||
        `The email is related to '${trimmedName}'`,
      value: trimmedName,
      count: 0,
      filterKey: 'objectiveClass',
      originalName: null,
      isEditing: false,
      tempName: trimmedName,
      tempDescription:
        newCategoryDescription.trim() ||
        `The email is related to '${trimmedName}'`,
    };
    setObjectiveClassItems((prev) => [...prev, newCategory]);
    setShowAddCategoryForm(false);
  };
  const handleNewCategoryDescriptionChange = (e) => {
    /* ... same ... */
    setNewCategoryDescription(e.target.value);
    setNewCategoryDescriptionManuallyEdited(true);
  };
  const handleCancelAddNewCategory = () => {
    /* ... same ... */
    setShowAddCategoryForm(false);
  };

  const handleToggleItemEdit = (itemId) => {
    /* ... same ... */
    setObjectiveClassItems((prevItems) =>
      prevItems.map((item) => {
        if (item.id === itemId) {
          return {
            ...item,
            isEditing: !item.isEditing,
            tempName: item.name,
            tempDescription: item.description,
            originalName:
              item.originalName === null
                ? null
                : item.originalName || item.name,
          };
        }
        return { ...item, isEditing: false };
      }),
    );
  };
  const handleItemInputChange = (itemId, field, value) => {
    /* ... same ... */
    setObjectiveClassItems((prevItems) =>
      prevItems.map((item) =>
        item.id === itemId ? { ...item, [field]: value } : item,
      ),
    );
  };
  const handleSaveItemEdit = (itemId) => {
    /* ... same ... */
    setObjectiveClassItems((prevItems) =>
      prevItems.map((item) => {
        if (item.id === itemId) {
          const trimmedTempName = item.tempName?.trim();
          if (!trimmedTempName) {
            alert('Category name cannot be empty.');
            return item;
          }
          const otherItems = prevItems.filter((i) => i.id !== itemId);
          if (
            otherItems.some(
              (other) =>
                other.name.toLowerCase() === trimmedTempName.toLowerCase(),
            )
          ) {
            alert(`A category named "${trimmedTempName}" already exists.`);
            return item;
          }
          return {
            ...item,
            name: trimmedTempName,
            description: item.tempDescription.trim(),
            value: trimmedTempName,
            isEditing: false,
          };
        }
        return item;
      }),
    );
  };
  const handleCancelItemEdit = (itemId) => {
    /* ... same ... */
    setObjectiveClassItems((prevItems) =>
      prevItems.map((item) =>
        item.id === itemId
          ? {
              ...item,
              isEditing: false,
              tempName: item.name,
              tempDescription: item.description,
            }
          : item,
      ),
    );
  };
  const handleDeleteItem = (itemId) => {
    /* ... same ... */
    setObjectiveClassItems((prevItems) =>
      prevItems.filter((item) => item.id !== itemId),
    );
  };

  // --- Restore Garbage Category ---
  const handleRestoreGarbageCategory = (garbageItemToRestore) => {
    if (
      objectiveClassItems.some(
        (item) =>
          item.name.toLowerCase() === garbageItemToRestore.name.toLowerCase(),
      )
    ) {
      alert(
        `An active category named "${garbageItemToRestore.name}" already exists.`,
      );
      return;
    }
    const restoredItem = {
      id: `${Date.now()}-restored-${garbageItemToRestore.name}`,
      name: garbageItemToRestore.name,
      description: garbageItemToRestore.description,
      value: garbageItemToRestore.name,
      count: garbageItemToRestore.count, // Use count from garbage data
      filterKey: 'objectiveClass',
      originalName: garbageItemToRestore.name, // It was a known entity
      isEditing: false,
      tempName: garbageItemToRestore.name,
      tempDescription: garbageItemToRestore.description,
    };
    setObjectiveClassItems((prev) => [...prev, restoredItem]);
    // The garbage list will appear shorter because `displayedGarbageCategories` filters out active items.
  };

  const actuallyProceedWithSave = async () => {
    /* ... same ... */
    const payload = objectiveClassItems.map(({ name, description }) => ({
      name,
      description,
    }));

    try {
      let BASE_URL = '';
      if (isGmailAuthenticated && !isOutlook) {
        BASE_URL = `${API_URL}/gmail_emails/email/objectives`;
      } else if (isOutlookAuthenticated && isOutlook) {
        BASE_URL = `${API_URL}/outlook_mails/email/objectives`;
      }
      if (!BASE_URL) {
        throw new Error('No Email App Authenticated');
      }
      const response = await fetch(BASE_URL, {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-type': 'application/json; charset=UTF-8',
          Authorization: `Bearer ${userGoogleId}`,
        },
        credentials: 'include',
      });
      if (!response.ok)
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      await response.json();
      toast.success('Changes saved successfully!');
      setObjectiveClassItems((items) =>
        items.map((item) => ({
          ...item,
          originalName: item.name,
          isEditing: false,
        })),
      );
      setIsManagingCategories(false);
      setShowGarbageUI(false); // Close garbage UI on successful save
    } catch (error) {
      toast.error(
        `Failed to save changes: ${typeof error === 'string' ? error : error.message || error.detail}`,
      );
    }
  };
  const handleSaveChangesToServer = () => {
    /* ... same ... */
    const hasMeaningfulChanges =
      objectiveClassItems.some((item) => {
        const serverVersion = configuredObjectivesFromServer.find(
          (co) => co.name === item.originalName,
        );
        if (item.originalName === null) return true;
        if (!serverVersion && item.originalName) {
          // Item was from server but server list no longer has it by originalName (means it was deleted from server, but user might have it locally or restored it)
          // This logic needs care: if originalName is set, it means it originated from server at some point.
          // If it's not in current `configuredObjectivesFromServer` using originalName, it's effectively a "new" add from user perspective or a re-add.
          return true; // Count as change if it's being (re)added or name changed from a server original
        }
        if (
          serverVersion &&
          (item.name !== serverVersion.name ||
            item.description !== serverVersion.description)
        )
          return true;
        return false;
      }) ||
      objectiveClassItems.length !== configuredObjectivesFromServer.length;

    if (
      !hasMeaningfulChanges &&
      objectiveClassItems.length === configuredObjectivesFromServer.length
    ) {
      alert('No changes to save.');
      setIsManagingCategories(false);
      return;
    }

    let warningMsg = 'You are about to save changes to categories.';
    const renamedItemsCount = objectiveClassItems.filter(
      (item) =>
        item.originalName &&
        item.originalName !== null &&
        item.name !== item.originalName,
    ).length;

    if (renamedItemsCount > 0) {
      warningMsg += `\n\n${renamedItemsCount} categor${renamedItemsCount > 1 ? 'ies have' : 'y has'} been renamed. Renaming acts like deleting the old category and creating a new one.`;
    }
    warningMsg +=
      '\nThis may require data resynchronization. Proceed with saving?';

    setModalWarningMessage(warningMsg);
    setShowSaveConfirmModal(true);
  };

  const renderItem = (item) => {
    /* ... (mostly same, key is item.id) ... */
    return (
      // eslint-disable-next-line
      <div
        key={item.id}
        className={`group flex w-full items-center justify-between gap-4 rounded-lg px-2 py-1 transition-all duration-200
        ${
          emailfilter[item?.filterKey] === item?.value
            ? 'bg-primary-gradient text-white'
            : 'text-white'
        }
        ${isManagingCategories && item.filterKey === 'objectiveClass' ? 'hover:bg-zinc-700' : !isManagingCategories ? 'cursor-pointer' : ''}
      `}
        onClick={() => {
          if (!isManagingCategories && item.filterKey !== 'objectiveClass') {
            handleFilterChange(item?.filterKey, item?.value);
          } else if (
            !isManagingCategories &&
            item.filterKey === 'objectiveClass'
          ) {
            handleFilterChange(item?.filterKey, item?.value);
          }
        }}
      >
        {item.isEditing && item.filterKey === 'objectiveClass' ? (
          <div className="flex flex-grow flex-col gap-1 py-1">
            <input
              type="text"
              value={item.tempName}
              placeholder="Category Name"
              onChange={(e) =>
                handleItemInputChange(item.id, 'tempName', e.target.value)
              }
              className="w-full rounded border border-zinc-600 bg-zinc-800 p-1 text-sm text-white placeholder-zinc-400 focus:border-[#5652CF] focus:ring-0"
            />
            <textarea
              value={item.tempDescription}
              placeholder="Description"
              onChange={(e) =>
                handleItemInputChange(
                  item.id,
                  'tempDescription',
                  e.target.value,
                )
              }
              className="mt-1 w-full rounded border border-zinc-600 bg-zinc-800 p-1 text-sm text-white placeholder-zinc-400 focus:border-[#5652CF] focus:ring-0"
              rows="2"
            />
            <div className="mt-1 flex items-center justify-end gap-2">
              <button
                onClick={() => handleCancelItemEdit(item.id)}
                className="p-1 text-zinc-400 hover:text-white"
                title="Cancel Edit"
              >
                <FiX size="1.1em" />
              </button>
              <button
                onClick={() => handleSaveItemEdit(item.id)}
                className="p-1 text-green-500 hover:text-green-400"
                title="Save Item Changes"
              >
                <FiCheckSquare size="1.1em" />
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex min-w-0 flex-grow items-center">
              {isManagingCategories && item.filterKey === 'objectiveClass' && (
                <>
                  <button
                    onClick={() => handleToggleItemEdit(item.id)}
                    className="mr-1.5 rounded p-0.5 text-blue-400 hover:bg-zinc-700 hover:text-blue-300"
                    title="Edit Category"
                  >
                    <FiEdit3 size="0.9em" />
                  </button>
                  <button
                    onClick={() => handleDeleteItem(item.id)}
                    className="mr-1.5 rounded p-0.5 text-red-500 hover:bg-zinc-700 hover:text-red-400"
                    title={`Delete Category "${item.name}"`}
                  >
                    <FiTrash2 size="0.9em" />
                  </button>
                </>
              )}
              <span
                className={`break-words text-sm font-semibold ${!isManagingCategories ? 'cursor-pointer' : 'cursor-default'}`}
                title={
                  item.description
                    ? `${item.name}\nDescription: ${item.description}`
                    : item.name
                }
              >
                {item.name}
              </span>
            </div>
            <span className="flex-shrink-0 rounded-full border-[0.5px] border-gray-400 px-2 py-1 text-xs">
              {item.count}
            </span>
          </>
        )}
      </div>
    );
  };

  // Filtered list of garbage categories that are not currently active
  const displayedGarbageCategories = garbageCategoriesList.filter(
    (gItem) =>
      !objectiveClassItems.some((activeItem) => activeItem.name === gItem.name),
  );

  const listContainerMaxHeight = 'max-h-[50vh]'; // Adjusted for potential extra buttons

  return (
    <div className="flex w-48 flex-col  text-xs sm:w-60">
      <div className="flex flex-shrink-0 items-center justify-between px-2 pb-2 pt-1">
        <span className="text-base font-semibold text-white">Categories</span>
        <button
          onClick={handleToggleManagingCategories}
          className="rounded p-1 text-zinc-400 transition-colors hover:text-white"
          title={
            isManagingCategories
              ? 'View Categories & Discard Unsaved Changes'
              : 'Manage Categories'
          }
        >
          {isManagingCategories ? (
            <FiX size="1.25em" />
          ) : (
            <FiSettings size="1.25em" />
          )}
        </button>
      </div>

      <div
        className={`scrollbar-none flex flex-col gap-2 overflow-y-auto pr-0.5 ${listContainerMaxHeight}`}
      >
        {isManagingCategories ? (
          <>
            {/* Active Objective Classes for Editing */}
            {objectiveClassItems.filter(
              (item) => item.filterKey === 'objectiveClass',
            ).length === 0 &&
              !showAddCategoryForm &&
              !showGarbageUI && (
                <p className="px-2 py-4 text-center text-xs text-zinc-500">
                  No active categories. Add new or view deleted ones.
                </p>
              )}
            {objectiveClassItems.map((item) =>
              item.filterKey === 'objectiveClass' ? renderItem(item) : null,
            )}

            {/* Add New Category Form Toggle & Form */}
            {!showAddCategoryForm && !showGarbageUI && (
              <button
                onClick={handleShowAddCategoryForm} /* ... same styling ... */
                className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-zinc-600 px-2 py-2 text-sm text-zinc-300 transition-all hover:border-zinc-500 hover:bg-zinc-700"
              >
                <FiPlus /> Add New Category
              </button>
            )}
            {showAddCategoryForm /* ... same form JSX ... */ && (
              <div className="my-2 rounded-lg border border-zinc-700 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-white">
                    Add New Category
                  </h4>
                  <button
                    onClick={handleCancelAddNewCategory}
                    className="p-1 text-zinc-400 hover:text-white"
                    title="Close Add Form"
                  >
                    <FiX size="1.1em" />
                  </button>
                </div>
                <input
                  type="text"
                  placeholder="Category Name"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  className="mb-2 w-full rounded border border-zinc-600 bg-transparent p-2 text-sm text-white placeholder-zinc-400 focus:border-[#5652CF] focus:ring-0"
                />
                <textarea
                  placeholder="Description (auto-updates with name)"
                  value={newCategoryDescription}
                  onChange={handleNewCategoryDescriptionChange}
                  className="mb-3 w-full rounded border border-zinc-600 bg-transparent p-2 text-sm text-white placeholder-zinc-400 focus:border-[#5652CF] focus:ring-0"
                  rows="3"
                />
                <div className="flex justify-end">
                  <button
                    onClick={handleAddNewCategory}
                    className="flex items-center gap-1 rounded border border-[#5652CF] px-3 py-1.5 text-xs text-blue-400 transition-colors hover:bg-[#5652CF] hover:text-white"
                  >
                    <FiSave /> Add Category
                  </button>
                </div>
              </div>
            )}

            {/* Toggle and Display for Garbage Categories */}
            {!showAddCategoryForm && ( // Don't show garbage toggle if add form is open
              <button
                onClick={() => setShowGarbageUI((prev) => !prev)}
                className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-zinc-600 px-2 py-2 text-sm text-zinc-300 transition-all hover:border-zinc-500 hover:bg-zinc-700"
              >
                {showGarbageUI ? (
                  <FiInbox className="mr-1" />
                ) : (
                  <FiArchive className="mr-1" />
                )}
                {showGarbageUI ? 'Hide Deleted' : 'Show Deleted'} (
                {displayedGarbageCategories.length})
              </button>
            )}

            {showGarbageUI && (
              <div className="mt-2 rounded-lg border border-zinc-700 p-2">
                <h5 className="mb-2 px-1 text-xs font-semibold text-zinc-400">
                  Deleted Categories (Restore)
                </h5>
                {displayedGarbageCategories.length === 0 && (
                  <p className="py-2 text-center text-xs text-zinc-500">
                    No deleted categories to show.
                  </p>
                )}
                {displayedGarbageCategories.map((gItem) => (
                  <div
                    key={gItem.id}
                    className="group flex items-center justify-between rounded p-1.5 hover:bg-zinc-700"
                  >
                    <div className="min-w-0 flex-grow">
                      <p
                        className="truncate text-sm text-white"
                        title={`${gItem.name}\n${gItem.description}`}
                      >
                        {gItem.name}
                      </p>
                      <p
                        className="truncate text-xs text-zinc-400"
                        title={gItem.description}
                      >
                        {gItem.description || 'No description'}
                      </p>
                    </div>
                    <div className="ml-2 flex flex-shrink-0 items-center">
                      <span className="mr-2 text-xs text-zinc-500">
                        ({gItem.count})
                      </span>
                      <button
                        onClick={() => handleRestoreGarbageCategory(gItem)}
                        className="p-1 text-green-500 opacity-70 hover:text-green-400 group-hover:opacity-100"
                        title={`Restore "${gItem.name}"`}
                      >
                        <FiPlusCircle size="1.1em" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Save All Changes Button */}
            {(objectiveClassItems.filter(
              (item) => item.filterKey === 'objectiveClass',
            ).length > 0 ||
              (displayedGarbageCategories.length > 0 && showGarbageUI)) && ( // Show if there are active items or garbage items potentially modified
              <button
                onClick={handleSaveChangesToServer}
                className="mb-2 mt-4 flex w-full items-center justify-center gap-1.5 rounded-lg bg-green-600 px-2 py-2 text-sm text-white transition-all hover:bg-green-500"
              >
                <FiSave /> Save All Changes
              </button>
            )}
          </>
        ) : (
          allDisplayItems.map((item) => renderItem(item))
        )}
      </div>

      {/* Confirmation Modal (same as before) */}
      {showSaveConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4 transition-opacity duration-300 ease-in-out">
          <div className="mx-auto w-full max-w-md rounded-lg border border-zinc-700 bg-zinc-800 p-6 shadow-xl">
            <div className="mb-4 flex items-start">
              <FiAlertTriangle
                className="mr-3 flex-shrink-0 text-yellow-400"
                size="1.8em"
              />
              <h3 className="text-lg font-semibold text-white">
                Confirm Changes
              </h3>
            </div>
            <p className="mb-6 whitespace-pre-wrap text-sm text-zinc-300">
              {modalWarningMessage}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowSaveConfirmModal(false)} /* Cancel */
                className="rounded border border-zinc-600 px-4 py-2 text-sm text-zinc-300 transition-colors hover:border-zinc-500 hover:bg-zinc-700"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowSaveConfirmModal(false);
                  actuallyProceedWithSave();
                }} /* Proceed */
                className="rounded bg-blue-600 px-4 py-2 text-sm text-white transition-colors hover:bg-[#5652CF] focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-50"
              >
                Proceed & Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailLabels;
